/**
 * Super Admin — Sync Schedules & API keys
 *
 * Per-workspace automatic sync cadence (every N minutes) + the master API keys
 * that external systems (N8N) use on POST /api/sync/trigger. The worker reads the
 * schedules and drains the queue; nothing here runs a sync — it needs the worker.
 *
 * Not a security boundary — it renders for anyone who reaches the route, but
 * sync_schedules is master-only via RLS and /api/sync/keys is behind
 * authorizeMaster, so a non-master reads nothing and every write is refused.
 *
 * @module pages/admin/SyncSchedules
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldAlert, Copy, Check, History } from "lucide-react";
import { Link } from "react-router-dom";
import { YDMPageHero } from "../../components/brand";
import { listWorkspaces, type AdminWorkspace } from "../../services/adminService";
import {
  listSyncSchedules,
  saveSyncSchedule,
  MIN_INTERVAL_MINUTES,
  type SyncScheduleRow,
} from "../../services/syncScheduleService";
import {
  listSyncApiKeys,
  createSyncApiKey,
  revokeSyncApiKey,
  type SyncApiKey,
} from "../../services/syncApiKeyService";

const DEFAULT_INTERVAL = 360; // 6h — a sensible starting cadence for a new schedule

interface Draft {
  intervalMinutes: number;
  enabled: boolean;
}

export const hint = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  // Segments joined rather than concatenated: with h === 0 the first segment
  // collapsed to "" while the second kept its leading space, so 15 minutes
  // rendered "≈  15m" — .trim() only strips the ends.
  const parts = [h > 0 ? `${h}h` : "", m > 0 ? `${m}m` : ""].filter(Boolean);
  return `≈ ${parts.join(" ")}`;
};

const AdminSyncSchedules: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<AdminWorkspace[]>([]);
  const [schedules, setSchedules] = useState<Record<string, SyncScheduleRow>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // API keys
  const [keys, setKeys] = useState<SyncApiKey[]>([]);
  const [keysError, setKeysError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ label: string | null; key: string } | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const copy = (text: string): void => {
    void navigator.clipboard?.writeText(text);
    setCopied(text);
  };

  const loadKeys = useCallback(async () => {
    const r = await listSyncApiKeys();
    if (r.success) setKeys(r.data ?? []);
    else setKeysError(r.error ?? "Failed to load keys");
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const [ws, sc] = await Promise.all([listWorkspaces(), listSyncSchedules()]);
    if (!ws.success) {
      setError(ws.error ?? "Failed to load workspaces");
      if (!silent) setLoading(false);
      return;
    }
    const scheduleMap = sc.success ? (sc.data ?? {}) : {};
    // Every workspace, fixtures and test leftovers included. Leaving the
    // synthetic ones out made this list shorter than the system, and a reader
    // cannot tell a filter from missing data; a schedule on a workspace with
    // nothing to sync is harmless (it enqueues nothing and is never stamped).
    const all = ws.data ?? [];
    setWorkspaces(all);
    setSchedules(scheduleMap);
    setDrafts(
      Object.fromEntries(
        all.map((w) => {
          const s = scheduleMap[w.id];
          return [w.id, { intervalMinutes: s?.intervalMinutes ?? DEFAULT_INTERVAL, enabled: s?.enabled ?? false }];
        }),
      ),
    );
    setError(sc.success ? null : (sc.error ?? "Failed to load schedules"));
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    void loadKeys();
  }, [load, loadKeys]);

  const patchDraft = (id: string, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const isDirty = useMemo(
    () => (id: string) => {
      const d = drafts[id];
      const s = schedules[id];
      if (!d) return false;
      if (!s) return true;
      return d.intervalMinutes !== s.intervalMinutes || d.enabled !== s.enabled;
    },
    [drafts, schedules],
  );

  const handleSave = async (id: string) => {
    const d = drafts[id];
    if (!d) return;
    setSavingId(id);
    const result = await saveSyncSchedule(id, d);
    if (result.success) {
      setError(null);
      await load(true);
    } else {
      setError(result.error ?? "Failed to save schedule");
    }
    setSavingId(null);
  };

  const handleCreateKey = async () => {
    setCreating(true);
    setKeysError(null);
    const r = await createSyncApiKey(newLabel.trim());
    if (r.success && r.data) {
      setCreatedKey({ label: r.data.label, key: r.data.key });
      setNewLabel("");
      await loadKeys();
    } else {
      setKeysError(r.error ?? "Failed to create key");
    }
    setCreating(false);
  };

  const handleRevokeKey = async (id: string) => {
    setRevokingId(id);
    const r = await revokeSyncApiKey(id);
    if (r.success) {
      setKeysError(null);
      await loadKeys();
    } else {
      setKeysError(r.error ?? "Failed to revoke key");
    }
    setRevokingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-ink-900 animate-spin" />
      </div>
    );
  }

  if (error && workspaces.length === 0) {
    return (
      <div className="space-y-4 animate-fade-in">
        <YDMPageHero eyebrow="Super Admin" headline="SYNC SCHEDULES" subtitle="Automatic sync cadence per workspace." />
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">This area is for the platform master</h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <YDMPageHero
        eyebrow="Super Admin"
        headline="SYNC SCHEDULES"
        subtitle="Automatic sync cadence per workspace."
        actions={
          /* This page says what WILL run; the history says what DID. They are one
             subject, so the history is reached from here rather than from a second
             row in the admin menu. */
          <Link
            to="/admin/sync-history"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-ink-950 text-white hover:bg-ink-900"
          >
            <History size={13} />
            ประวัติการ sync
          </Link>
        }
      />

      {/* API keys */}
      <div className="space-y-3">
        <div>
          <h2 className="text-base font-semibold text-ink-900">API keys</h2>
          <p className="text-sm text-neutral-700">
            For N8N / external triggers: <code className="text-xs">POST /api/sync/trigger</code> with{" "}
            <code className="text-xs">Authorization: Bearer &lt;key&gt;</code> and body{" "}
            <code className="text-xs">{`{ "workspace_id": "…", "all": true }`}</code>.
          </p>
        </div>

        {createdKey && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 space-y-2">
            <p className="text-sm font-medium text-green-900">
              Key created{createdKey.label ? ` — ${createdKey.label}` : ""}. Copy it now — it won&apos;t be shown again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all bg-white border border-green-200 rounded px-2 py-1 text-xs font-mono">
                {createdKey.key}
              </code>
              <button
                type="button"
                onClick={() => copy(createdKey.key)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-ink-950 hover:bg-primary-700"
              >
                {copied === createdKey.key ? <Check size={13} /> : <Copy size={13} />}
                Copy
              </button>
              <button
                type="button"
                onClick={() => setCreatedKey(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {keysError && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-4 py-3 text-sm">{keysError}</div>
        )}

        <div className="bg-white rounded-lg border border-neutral-200 p-4 flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="new-key-label" className="block text-xs text-neutral-700 mb-1">
              New key label
            </label>
            <input
              id="new-key-label"
              type="text"
              value={newLabel}
              disabled={creating}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreateKey();
              }}
              placeholder="e.g. n8n prod"
              className="w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
            />
          </div>
          <button
            type="button"
            onClick={() => void handleCreateKey()}
            disabled={creating}
            className="px-4 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-ink-950 hover:bg-primary-700 disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create key"}
          </button>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-700">
              <tr>
                <th className="px-4 py-3 font-medium">Label</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Last used</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium sr-only">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {keys.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-neutral-700">
                    No keys yet.
                  </td>
                </tr>
              ) : (
                keys.map((k) => (
                  <tr key={k.id}>
                    <td className="px-4 py-3 text-ink-900">{k.label ?? <span className="text-neutral-700">(none)</span>}</td>
                    <td className="px-4 py-3 text-neutral-700">{new Date(k.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-neutral-700">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {k.revoked_at ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-xs">
                          revoked
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!k.revoked_at && (
                        <button
                          type="button"
                          onClick={() => void handleRevokeKey(k.id)}
                          disabled={revokingId === k.id}
                          className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          {revokingId === k.id ? "Revoking…" : "Revoke"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3 text-sm text-neutral-700">
        Enabled workspaces sync every N minutes. This only schedules — a sync worker must be running to
        actually process the jobs.
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-4 py-3 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-neutral-700">
            <tr>
              <th className="px-4 py-3 font-medium">Workspace</th>
              <th className="px-4 py-3 font-medium">Every (minutes)</th>
              <th className="px-4 py-3 font-medium">Enabled</th>
              {/* The stored value is the scheduler's checkpoint, stamped before any
                  job executes (even when every account was already busy) — not
                  the time a sync ran. A workspace with no account that can sync
                  (none connected, or only demo / credential-less ones) is never
                  stamped, so the cell stays empty however long the schedule is on. */}
              <th className="px-4 py-3 font-medium" title="When the scheduler last queued this workspace's accounts — not when a sync last completed. Stays empty while the workspace has no account that can sync (none connected, or only demo / unconnected ones).">Last checked</th>
              <th className="px-4 py-3 font-medium sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {workspaces.map((w) => {
              const d = drafts[w.id] ?? { intervalMinutes: DEFAULT_INTERVAL, enabled: false };
              const s = schedules[w.id];
              return (
                <tr key={w.id}>
                  <td className="px-4 py-3 text-ink-900">
                    <div>{w.name}</div>
                    <button
                      type="button"
                      onClick={() => copy(w.id)}
                      title="Copy workspace ID (for N8N)"
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-neutral-700 hover:text-neutral-700 font-mono"
                    >
                      {copied === w.id ? <Check size={11} /> : <Copy size={11} />}
                      {w.id}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min={MIN_INTERVAL_MINUTES}
                      step={5}
                      value={d.intervalMinutes}
                      aria-label={`Interval minutes for ${w.name}`}
                      onChange={(e) => patchDraft(w.id, { intervalMinutes: Number(e.target.value) })}
                      className="w-24 text-sm border border-neutral-300 rounded-md px-2 py-1"
                    />
                    <span className="ml-2 text-xs text-neutral-700">{hint(d.intervalMinutes)}</span>
                    {/* A stored 360 and an unset 360 rendered identically; the
                        latter is only this form's default. */}
                    {!s && <span className="ml-2 text-xs text-neutral-700 italic">ยังไม่ตั้งค่า</span>}
                  </td>
                  <td className="px-4 py-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        aria-label={`Enable schedule for ${w.name}`}
                        onChange={(e) => patchDraft(w.id, { enabled: e.target.checked })}
                        className="h-4 w-4"
                      />
                      <span className="text-xs text-neutral-700">{d.enabled ? "On" : "Off"}</span>
                    </label>
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {s?.lastRunAt ? new Date(s.lastRunAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void handleSave(w.id)}
                      disabled={savingId === w.id || !isDirty(w.id) || d.intervalMinutes < MIN_INTERVAL_MINUTES}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-ink-950 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingId === w.id ? "Saving…" : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSyncSchedules;
